import { useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common";
import { VorlagenPicker } from "@/components/VorlagenPicker";
import { api } from "@/lib/api";
import { mitTestkundeRueckfrage } from "@/lib/testkundeRueckfrage";
import MailKundenSuche, { kundeAnzeigename } from "@/components/MailKundenSuche";

/**
 * MailSchreibenModal (A-010 M1, 16.10.2026) — „Antworten“ / „Neue Mail“ aus dem Mailverlauf.
 * Fenster über Kunde/Werkbank; gesendet wird über /module-mail-inbox/senden
 * (SMTP aus Einstellungen, feste Firmen-Signatur, Kopie in „Gesendet“, Protokoll mit Kunde + Projekt).
 * Props: { isOpen, onClose, kundeId, mail? (Eintrag aus dem Mailverlauf → Antwort), projekte?, projektId?, onGesendet? }
 * Mail-Zentrale (A-010 M3), alle optional: ohneKunde (Antwort an unbekannten Absender), weiterleiten (mail weiterleiten,
 * Anhänge wählbar), kundeWaehlen (Neue Mail mit Kundensuche: füllt An, Anrede, Projektliste).
 * M3b (Führung): Ist „An“ leer, ungültig oder eine Systemadresse, steht sofort darunter warum und was zu tun ist;
 * „Senden“ nennt, was noch fehlt. Optional: titel (Fenstertitel, z. B. „Mail an Kunden“).
 * A-017: vorlage (Textvorlage, z. B. „Danke für Ihre Bilder“) füllt den Mailtext vor, Platzhalter mit Kundendaten.
 */
const ADRESSE_OK = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

// Was stimmt mit „An“ nicht? -> { art: "fehler" | "info", text } oder null
export const anPruefen = (an, entwurf, { weiterleiten = false, kundeWaehlen = false } = {}) => {
  const a = (an || "").trim();
  if (!a) {
    return { art: "fehler", text: entwurf?.an_hinweis
      || (kundeWaehlen ? "Noch kein Empfänger: oben einen Kunden suchen oder die Adresse hier eintragen."
        : weiterleiten ? "Bitte die Adresse eintragen, an die weitergeleitet werden soll."
          : "Bitte eine Empfängeradresse eintragen.") };
  }
  if (/[,;]/.test(a) || /\s/.test(a)) return { art: "fehler", text: "Bitte genau eine Adresse eintragen (keine Liste, keine Leerzeichen)." };
  if (!ADRESSE_OK.test(a)) return { art: "fehler", text: `„${a}“ ist keine vollständige Mail-Adresse (Beispiel: name@beispiel.de).` };
  const gesperrt = !weiterleiten && (entwurf?.sperrliste || []).find((x) => x && a.toLowerCase().includes(x));
  if (gesperrt) {
    const grund = gesperrt.startsWith("@") ? `eigene Domain ${gesperrt}` : `Sperrliste „${gesperrt}“`;
    return { art: "fehler", text: `„${a}“ ist eine Systemadresse (${grund}), z. B. die Suite selbst oder ein Formular-Absender. `
      + "Dorthin kann nicht geantwortet werden. Bitte die Kundenadresse eintragen." };
  }
  if (entwurf?.an_hinweis && entwurf?.an_quelle === "kunde" && a === (entwurf.an || "")) return { art: "info", text: entwurf.an_hinweis };
  return null;
};
const vorlageAlsText = (htmlText) => {
  const div = document.createElement("div");
  div.innerHTML = String(htmlText || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n");
  return (div.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

// Textvorlage → Mailtext (A-010 M1b): Platzhalter {x} und [x] mit Kundendaten ersetzen.
// Enthält die Vorlage eine Anrede, ersetzt sie die vorausgefüllte; sonst wird die Anrede vorangestellt.
const PLATZHALTER = ["anrede_brief", "kunde_name", "kunde_email", "kunde_telefon", "kunde_adresse", "datum", "bearbeiter"];
export const vorlageFuellen = (roh, entwurf) => {
  const anrede = entwurf?.anrede || "Sehr geehrte Damen und Herren";
  let t = String(roh || "");
  PLATZHALTER.forEach((n) => { t = t.replace(new RegExp(`\\[${n}\\]`, "g"), `{${n}}`); });
  t = t.replace(/Sehr geehrte[r]?(\/r)? \{anrede_brief\}( \{kunde_name\})?/g, "{anrede_brief}");
  const hatAnrede = t.includes("{anrede_brief}") || /^\s*(Sehr geehrte|Hallo|Guten (Tag|Morgen|Abend)|Liebe[r]?\s|Moin)/i.test(t);
  t = t
    .replace(/\{anrede_brief\}/g, anrede)
    .replace(/\{kunde_name\}/g, entwurf?.kunde_name || "")
    .replace(/\{kunde_email\}/g, entwurf?.kunde_email || "")
    .replace(/\{kunde_telefon\}/g, entwurf?.kunde_telefon || "")
    .replace(/\{kunde_adresse\}/g, entwurf?.kunde_adresse || "")
    .replace(/\{datum\}/g, new Date().toLocaleDateString("de-DE"))
    .replace(/\{bearbeiter\}/g, "Ihre Tischlerei Graupner");
  return hatAnrede ? t : `${anrede},\n\n${t}`;
};

const MailSchreibenModal = ({ isOpen, onClose, kundeId, mail = null, projekte = [], projektId = "", onGesendet,
  ohneKunde = false, weiterleiten = false, kundeWaehlen = false, titel = "", vorlage = "" }) => {
  const [laden, setLaden] = useState(false);
  const [senden, setSenden] = useState(false);
  const [entwurf, setEntwurf] = useState(null);
  const [an, setAn] = useState("");
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  const [projekt, setProjekt] = useState("");
  const [zitatAnhaengen, setZitatAnhaengen] = useState(true);
  // Mail-Zentrale (M3): gewählter Kunde bei „Neue Mail“, dessen Projekte, Anhänge beim Weiterleiten
  const [gewaehlt, setGewaehlt] = useState(null);
  const [eigeneProjekte, setEigeneProjekte] = useState([]);
  const [anhAuswahl, setAnhAuswahl] = useState({});
  // A-018 Teil 2: Kopie-Empfänger (Vorbelegung vom Server aus den Adressen des Kunden, „immer in Kopie“)
  const [cc, setCc] = useState([]);
  const [ccNeu, setCcNeu] = useState("");
  const [ccFehler, setCcFehler] = useState("");
  const startText = useRef("");
  const kid = kundeId || gewaehlt?.id || "";
  const projektListe = kundeWaehlen && !kundeId ? eigeneProjekte : projekte;

  const startProjekt = projektId || (projektListe.length === 1 ? projektListe[0].id : "");

  useEffect(() => {
    if (!isOpen) return;
    setGewaehlt(null);
    setEigeneProjekte([]);
    setEntwurf(null);
    setText("");
    setAn("");
    setBetreff("");
    setCc([]);
    setCcNeu("");
    setCcFehler("");
    startText.current = "";
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || (!kid && !ohneKunde && !kundeWaehlen)) return;
    let abbruch = false;
    if (!entwurf || !kundeWaehlen) setLaden(true);
    if (!kundeWaehlen) setEntwurf(null);
    setProjekt(startProjekt);
    setZitatAnhaengen(true);
    api.post("/module-mail-inbox/mail-entwurf", {
      kunde_id: kid || undefined,
      projekt_id: startProjekt || undefined,
      ...(mail ? { account_id: mail.account_id, folder: mail.folder, uid: mail.uid } : {}),
      ...(weiterleiten ? { weiterleiten: true } : {}),
    })
      .then((r) => {
        if (abbruch) return;
        setEntwurf(r.data);
        setAn((alt) => (kundeWaehlen && !r.data.an ? alt : r.data.an || ""));
        setCc(r.data.cc || []);
        setBetreff((alt) => (kundeWaehlen && alt ? alt : r.data.betreff || ""));
        const neu = r.data.weiterleiten
          ? "\n\n\nMit freundlichen Grüßen"
          : vorlage
            ? vorlageFuellen(vorlageAlsText(vorlage), r.data)
            : `${r.data.anrede || "Sehr geehrte Damen und Herren"},\n\n\n\nMit freundlichen Grüßen`;
        // Text nur ersetzen, solange er nicht von Hand geändert wurde (Kundenwahl bei „Neue Mail“)
        const vorher = startText.current;
        setText((alt) => (!alt || alt === vorher ? neu : alt));
        startText.current = neu;
        setAnhAuswahl(Object.fromEntries((r.data.anhaenge || []).map((a) => [a.index, !a.klein])));
      })
      .catch((err) => {
        if (abbruch) return;
        toast.error(err?.response?.data?.detail || "Mail-Fenster laden fehlgeschlagen");
        onClose?.();
      })
      .finally(() => !abbruch && setLaden(false));
    return () => { abbruch = true; };
    // eslint-disable-next-line
  }, [isOpen, kid, mail?.uid, mail?.folder, mail?.account_id, weiterleiten]);

  const kundeGewaehlt = async (k) => {
    setGewaehlt(k);
    try {
      const r = await api.get("/module-projekte/", { params: { kunde_id: k.id } });
      const liste = (Array.isArray(r.data) ? r.data : []).map((p) => ({ id: p.id, titel: p.titel || p.title || "", status: p.status || "" }));
      setEigeneProjekte(liste);
      // M3c: genau ein offenes Projekt → vorauswählen, Betreff = Projekttitel (wenn noch leer)
      const offen = liste.filter((p) => !/abgeschlossen|archiv|erledigt|storniert/i.test(p.status));
      const wahl = offen.length === 1 ? offen[0] : liste.length === 1 ? liste[0] : null;
      setProjekt(wahl ? wahl.id : "");
      if (wahl?.titel) setBetreff((alt) => alt || wahl.titel);
    } catch {
      setEigeneProjekte([]);
    }
  };

  const projektWechseln = (pid) => {
    setProjekt(pid);
    // Neue Mail: Betreff folgt dem Projekttitel, solange er nicht von Hand geändert wurde
    if (entwurf && !entwurf.antwort) {
      const alt = (projektListe.find((p) => p.id === projekt)?.titel) || entwurf.betreff || "";
      if (!betreff || betreff === alt) setBetreff(projektListe.find((p) => p.id === pid)?.titel || "");
    }
  };

  const vorlageEinfuegen = ({ content }) => {
    setText(vorlageFuellen(vorlageAlsText(content), entwurf));
  };

  const absenden = async () => {
    setSenden(true);
    try {
      const gewaehlteAnhaenge = (entwurf?.anhaenge || []).filter((a) => anhAuswahl[a.index]).map((a) => a.index);
      const r = await mitTestkundeRueckfrage((extra) => api.post("/module-mail-inbox/senden", {
        ...extra,
        an, betreff, text, cc,
        kunde_id: kid || undefined,
        projekt_id: projekt || null,
        ...(entwurf?.weiterleiten ? {
          weiterleiten: true,
          anhaenge_von: { account_id: mail.account_id, folder: mail.folder, uid: mail.uid, indices: gewaehlteAnhaenge },
        } : {}),
        zitat: entwurf?.antwort && zitatAnhaengen ? entwurf.zitat : "",
        zitat_kopf: entwurf?.zitat_kopf || "",
        in_reply_to: entwurf?.in_reply_to || "",
        references: entwurf?.references || "",
      }));
      toast.success(`Mail an ${an}${cc.length ? ` (Kopie: ${cc.join(", ")})` : ""} gesendet${r?.data?.anhaenge ? ` (${r.data.anhaenge} Anhang/Anhänge)` : ""}`);
      onGesendet?.();
      onClose?.();
    } catch (err) {
      if (!err?.abgebrochen) toast.error(err?.response?.data?.detail || "Senden fehlgeschlagen");
    } finally {
      setSenden(false);
    }
  };

  const adressen = entwurf?.adressen || [];
  const adressLabel = (a) => [a.adresse, a.gefunden_ueber && a.gefunden_ueber !== "Haupt-Mail" ? `(${a.gefunden_ueber})` : a.gefunden_ueber ? "(Haupt-Mail)" : ""].filter(Boolean).join(" ");
  const anWaehlen = (wert) => { setAn(wert); setCc((alt) => alt.filter((c) => c !== wert.trim().toLowerCase())); };
  const ccDazu = (wert) => {
    const a = (wert || "").trim().toLowerCase();
    if (!a) return;
    if (!ADRESSE_OK.test(a)) { setCcFehler(`„${a}“ ist keine vollständige Mail-Adresse.`); return; }
    if (a === an.trim().toLowerCase() || cc.includes(a)) { setCcFehler(`„${a}“ ist schon Empfänger.`); return; }
    setCcFehler("");
    setCc((alt) => [...alt, a]);
    setCcNeu("");
  };
  const ccAngebote = adressen.filter((a) => a.adresse !== an.trim().toLowerCase() && !cc.includes(a.adresse));
  const feld = "w-full border border-input rounded-sm px-2 py-1.5 text-sm bg-background";
  const anHinweis = entwurf ? anPruefen(an, entwurf, { weiterleiten: !!entwurf.weiterleiten, kundeWaehlen: kundeWaehlen && !kundeId }) : null;
  const fehlt = [
    anHinweis?.art === "fehler" && "gültige Empfängeradresse",
    !betreff.trim() && "Betreff",
    !text.trim() && "Text",
  ].filter(Boolean);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={titel || (weiterleiten ? "Mail weiterleiten" : mail ? "Mail beantworten" : "Neue Mail")} size="lg">
      {laden || !entwurf ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Mail wird vorbereitet…
        </div>
      ) : (
        <div className="space-y-2 text-sm" data-testid="mail-schreiben-modal">
          {kundeWaehlen && !kundeId && (
            <div className="block" data-testid="mail-kunde-waehlen">
              <span className="text-xs text-muted-foreground">
                Kunde {gewaehlt ? <b className="text-foreground">{kundeAnzeigename(gewaehlt)}</b> : "(tippen zum Suchen; füllt An, Anrede und Projektliste)"}
              </span>
              <MailKundenSuche onWaehle={kundeGewaehlt} autoFocus testid="mail-neu-kunde" />
            </div>
          )}
          <label className="block">
            <span className="text-xs text-muted-foreground">An</span>
            {adressen.length > 1 && !entwurf.weiterleiten && (
              <select className={`${feld} mb-1`} value={adressen.some((a) => a.adresse === an.trim().toLowerCase()) ? an.trim().toLowerCase() : ""}
                onChange={(e) => e.target.value && anWaehlen(e.target.value)} data-testid="mail-an-auswahl">
                {!adressen.some((a) => a.adresse === an.trim().toLowerCase()) && <option value="">– andere Adresse (unten) –</option>}
                {adressen.map((a) => <option key={a.adresse} value={a.adresse}>{adressLabel(a)}</option>)}
              </select>
            )}
            <input className={`${feld} ${anHinweis?.art === "fehler" ? "border-amber-500" : ""}`} value={an} onChange={(e) => setAn(e.target.value)} data-testid="mail-an" />
          </label>
          {anHinweis && (
            <div
              className={`text-xs rounded-sm px-2 py-1.5 border ${anHinweis.art === "fehler"
                ? "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                : "border-sky-200 bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-100"}`}
              data-testid={anHinweis.art === "fehler" ? "mail-an-hinweis" : "mail-an-info"}
            >
              {anHinweis.text}
            </div>
          )}
          <div data-testid="mail-cc-bereich">
            <span className="text-xs text-muted-foreground">Kopie (CC)</span>
            <div className="flex flex-wrap items-center gap-1.5 mt-0.5" data-testid="mail-cc-liste">
              {cc.length === 0 && <span className="text-xs text-muted-foreground">keine Kopie</span>}
              {cc.map((c, i) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 pl-2 pr-1 py-0.5 text-xs max-w-full" data-testid={`mail-cc-chip-${i}`}>
                  <span className="break-all">{c}</span>
                  <button type="button" onClick={() => setCc(cc.filter((x) => x !== c))} className="px-1 rounded hover:bg-muted" title="Kopie entfernen" data-testid={`mail-cc-entfernen-${i}`}>×</button>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {ccAngebote.length > 0 && (
                <select className="h-8 rounded-sm border border-input bg-background px-2 text-xs max-w-full" value="" onChange={(e) => e.target.value && ccDazu(e.target.value)} data-testid="mail-cc-auswahl">
                  <option value="">+ Kopie an … (aus den Adressen des Kunden)</option>
                  {ccAngebote.map((a) => <option key={a.adresse} value={a.adresse}>{adressLabel(a)}</option>)}
                </select>
              )}
              <input value={ccNeu} onChange={(e) => { setCcNeu(e.target.value); setCcFehler(""); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ccDazu(ccNeu); } }}
                placeholder="andere Kopie-Adresse" className="h-8 flex-1 min-w-[10rem] rounded-sm border border-input bg-background px-2 text-xs" data-testid="mail-cc-eingabe" />
              <button type="button" onClick={() => ccDazu(ccNeu)} className="h-8 px-2 text-xs border rounded-sm hover:bg-accent" data-testid="mail-cc-dazu">+ Kopie</button>
            </div>
            {ccFehler && <div className="text-xs text-amber-800 mt-1" data-testid="mail-cc-fehler">{ccFehler}</div>}
          </div>
          <label className="block">
            <span className="text-xs text-muted-foreground">Betreff</span>
            <input className={feld} value={betreff} onChange={(e) => setBetreff(e.target.value)} data-testid="mail-betreff" />
          </label>
          {projektListe.length > 0 && (
            <label className="block">
              <span className="text-xs text-muted-foreground">Projekt (für das Protokoll)</span>
              <select className={feld} value={projekt} onChange={(e) => projektWechseln(e.target.value)} data-testid="mail-projekt">
                <option value="">– ohne Projekt –</option>
                {projektListe.map((p) => (
                  <option key={p.id} value={p.id}>{p.titel || "(ohne Titel)"}</option>
                ))}
              </select>
            </label>
          )}
          {projektListe.length > 0 && !projekt && !entwurf.weiterleiten && (
            <div className="text-xs rounded-sm px-2 py-1.5 border border-sky-200 bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-100" data-testid="mail-projekt-hinweis">
              Bitte Projekt wählen, damit die Mail im Projekt-Protokoll steht.
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Text</span>
            <VorlagenPicker doc_type="mail_antwort" label="Textvorlage" compact={true} onSelect={vorlageEinfuegen} />
          </div>
          <textarea
            className={`${feld} min-h-[220px] font-sans`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="mail-text"
          />
          <div className="text-xs text-muted-foreground">Darunter folgt automatisch die Firmen-Signatur.</div>
          {entwurf.weiterleiten && (entwurf.anhaenge || []).length > 0 && (
            <div className="border-t pt-2 space-y-1" data-testid="mail-weiterleiten-anhaenge">
              <div className="text-xs text-muted-foreground">Anhänge mitsenden</div>
              {entwurf.anhaenge.map((a) => (
                <label key={a.index} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!anhAuswahl[a.index]} onChange={(e) => setAnhAuswahl({ ...anhAuswahl, [a.index]: e.target.checked })} />
                  {a.filename} <span className="text-muted-foreground">({Math.round((a.size || 0) / 1024)} KB{a.klein ? " · vermutlich Logo" : ""})</span>
                </label>
              ))}
            </div>
          )}
          {entwurf.antwort && (
            <div className="border-t pt-2 space-y-1" data-testid="mail-zitat-bereich">
              {entwurf.zitat ? (
                <>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={zitatAnhaengen} onChange={(e) => setZitatAnhaengen(e.target.checked)} data-testid="mail-zitat-an" />
                    {entwurf.weiterleiten ? "Weitergeleitete Mail unten anfügen" : "Originalmail zitiert anhängen"}
                  </label>
                  {zitatAnhaengen && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground select-none">
                        Zitierte Mail (wird unten angehängt): {entwurf.zitat_kopf}{" "}
                        <span className="italic">„{entwurf.zitat.replace(/\s+/g, " ").trim().slice(0, 80)}{entwurf.zitat.length > 80 ? "…" : ""}“</span>
                      </summary>
                      <pre className="mt-1 whitespace-pre-wrap bg-muted/40 rounded-sm p-2 border max-h-[20vh] overflow-auto break-words text-muted-foreground">
                        {entwurf.zitat_kopf}{"\n"}{entwurf.zitat}
                      </pre>
                    </details>
                  )}
                </>
              ) : (
                <div className="text-xs text-muted-foreground">Die Originalmail enthält keinen lesbaren Text; es wird nichts zitiert.</div>
              )}
            </div>
          )}
          <div className="flex flex-wrap justify-end items-center gap-2 pt-1">
            {fehlt.length > 0 && (
              <span className="text-xs text-amber-700 dark:text-amber-300 mr-auto" data-testid="mail-senden-grund">
                Senden geht erst mit: {fehlt.join(", ")}.
              </span>
            )}
            <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded-sm hover:bg-accent">Abbrechen</button>
            <button
              onClick={absenden}
              disabled={senden || fehlt.length > 0}
              title={fehlt.length ? `Es fehlt: ${fehlt.join(", ")}` : "Mail senden"}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:opacity-90 disabled:opacity-50"
              data-testid="btn-mail-senden"
            >
              {senden ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Senden
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MailSchreibenModal;
